// MapCanvas.tsx -- Canvas 2D rendering with d3-force physics.
// Per D-29, D-34, D-37, D-38, D-40, D-41, D-42, UI-SPEC Canvas Rendering.
// CRITICAL: Receives canvasRef as prop from MapSurface (does NOT create internal useRef for canvas).
// MapSurface owns canvasRef and passes it to BOTH MapCanvas and useMapInteraction.

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import {
  forceSimulation,
  forceManyBody,
  forceLink,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
  type Force,
} from 'd3-force';
import { siteColor } from '../../lib/sites';
import { clusterColor, buildClusterIndexMap, UNCLUSTERED_COLOR } from '../../lib/clusters';
import type { GraphNode, GraphEdge, Transform, MapSimulation } from './types';

interface MapCanvasProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  // Callback ref from MapSurface: writes canvasRef.current AND publishes the element as state,
  // so useMapInteraction re-attaches listeners whenever this component remounts (bug-451).
  attachCanvas: (el: HTMLCanvasElement | null) => void;
  nodes: GraphNode[];
  edges: GraphEdge[];
  simulationRef: React.MutableRefObject<MapSimulation | null>;
  nodesRef: React.MutableRefObject<GraphNode[]>;
  hoveredNodeId: string | null;
  adjacency: Map<string, Set<string>>;
  transform: Transform;
  viewMode: string;
  projectFilter: string | null;
  personFilter: string | null;
  personColors: Map<string, string> | null;
  isInteracting: boolean;
  // Theme-resolved canvas colors (Canvas 2D can't read CSS var()). Computed by MapSurface from
  // the live --tokens and re-read on theme flip, so the graph is legible in BOTH themes.
  themeColors: { edge: string; label: string; dimmed: string };
  // D5 (Ask): the source ids of the latest answer. When non-empty, every node/edge that is NOT a source
  // dims to the muted tone, so the answer's citations stand out on the map. Null/empty = no highlight.
  highlightedIds?: Set<string> | null;
}

// D-37: Ambient drift constants
const DRIFT_MAX_DISPLACEMENT = 0.3;
const DRIFT_FRAME_SKIP = 3; // Only apply drift every 3rd frame (~20fps at 60fps rAF)

// Zoom-adaptive labels: at k=1 threshold=80 (no labels), at k=2 threshold=40, at k=3 threshold=27
const LABEL_BASE_THRESHOLD = 80;
const LABEL_MAX_CHARS = 18;

// D-34: Dimmed opacity for non-connected nodes on hover
const DIM_OPACITY = 0.2;

// Repo-backbone edges carry a sub-threshold similarity (below the 0.70 semantic-link bar): a STRUCTURAL
// "same repo" link, drawn dashed + fainter so it reads as connective tissue, not a strong semantic tie.
const BACKBONE_THRESHOLD = 0.70;

// Size mapping: edge count → display radius 5-16px via sqrt scaling
function nodeRadius(edgeCount: number): number {
  const t = Math.min(edgeCount / 10, 1); // normalize 0-10+ → 0-1
  return 5 + Math.sqrt(t) * 11; // 5px (isolated) → 16px (hub)
}

// Cluster separation tuning. When one project dominates, the force layout collapses its
// sub-clusters (distinct project_id groups, already separated by COLOR) into a single blob.
// Two data-preserving levers spread them into legible islands:
//   1. Down-weight links that BRIDGE two different clusters, so the existing charge repulsion
//      pushes clusters apart instead of being out-pulled by cross-cluster similarity edges.
//   2. Gently pull each node toward its own cluster's centroid, keeping each island compact so
//      the repulsion concentrates and the gaps between islands stay crisp.
// These are dials: raise CROSS_CLUSTER toward INTRA for a tighter single map; raise GATHER for
// harder-edged islands. Setting GATHER to 0 disables the centroid pull entirely.
const INTRA_CLUSTER_LINK_STRENGTH = 0.6;   // links within one cluster: original cohesion, unchanged
const CROSS_CLUSTER_LINK_STRENGTH = 0.04;  // links bridging clusters: weak, so clusters can separate
const CLUSTER_GATHER_STRENGTH = 0.20;      // pull toward own-cluster centroid (0 disables)

// d3-force custom force: nudges each node toward the centroid of its own project_id cluster.
// Nodes with no project_id (unclustered) are left to float under charge/links alone.
function forceCluster(strength: number): Force<GraphNode, undefined> {
  let simNodes: GraphNode[] = [];
  const force = ((alpha: number) => {
    const centroids = new Map<string, { x: number; y: number; n: number }>();
    for (const node of simNodes) {
      const key = node.project_id;
      if (key == null || node.x == null || node.y == null) continue;
      const c = centroids.get(key) ?? { x: 0, y: 0, n: 0 };
      c.x += node.x;
      c.y += node.y;
      c.n += 1;
      centroids.set(key, c);
    }
    const k = strength * alpha;
    for (const node of simNodes) {
      const key = node.project_id;
      if (key == null || node.x == null || node.y == null) continue;
      const c = centroids.get(key)!;
      node.vx = (node.vx ?? 0) - (node.x - c.x / c.n) * k;
      node.vy = (node.vy ?? 0) - (node.y - c.y / c.n) * k;
    }
  }) as Force<GraphNode, undefined>;
  force.initialize = (n: GraphNode[]) => {
    simNodes = n;
  };
  return force;
}

export function MapCanvas({
  canvasRef,
  attachCanvas,
  nodes,
  edges,
  simulationRef,
  nodesRef,
  hoveredNodeId,
  adjacency,
  transform,
  viewMode,
  projectFilter,
  personFilter,
  personColors,
  isInteracting,
  themeColors,
  highlightedIds,
}: MapCanvasProps) {
  const rafRef = useRef<number | null>(null);
  const driftFrameRef = useRef(0);
  const driftActiveRef = useRef(false);
  const prevNodesIdRef = useRef<string>('');
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const renderRef = useRef<() => void>(() => {});

  // Compute edge count per node for sizing
  const edgeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of edges) {
      counts.set(e.source, (counts.get(e.source) || 0) + 1);
      counts.set(e.target, (counts.get(e.target) || 0) + 1);
    }
    return counts;
  }, [edges]);

  const clusterIndexMap = useMemo(() => buildClusterIndexMap(nodes), [nodes]);

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const currentNodes = nodesRef.current;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // Apply DPR scaling then transform
    ctx.scale(dpr, dpr);
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.k, transform.k);

    // Determine connected set for hover dimming (D-34)
    const connectedIds = new Set<string>();
    if (hoveredNodeId) {
      connectedIds.add(hoveredNodeId);
      const neighbors = adjacency.get(hoveredNodeId);
      if (neighbors) {
        neighbors.forEach(id => connectedIds.add(id));
      }
    }

    // Determine active filters and color mode
    const isSiteFilter = viewMode !== 'all' && viewMode !== 'topics';
    const viewSiteFilter = isSiteFilter ? viewMode : null;
    // Site colors only when source is the sole active filter
    const colorByCluster = !isSiteFilter || !!projectFilter;

    // D5 (Ask): when an answer's sources are known, dim everything that is not a source.
    const highlightActive = !!highlightedIds && highlightedIds.size > 0;

    // --- Draw edges ---
    for (const edge of edges) {
      const sourceNode = currentNodes.find(n => n.id === edge.source);
      const targetNode = currentNodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) continue;
      if (sourceNode.x == null || targetNode.x == null) continue;

      const isConnectedEdge = hoveredNodeId && connectedIds.has(edge.source) && connectedIds.has(edge.target);

      // D-34: Dim non-connected edges on hover
      if (hoveredNodeId && !isConnectedEdge) {
        ctx.globalAlpha = DIM_OPACITY;
      } else {
        ctx.globalAlpha = 1;
      }

      // Dim edges where neither endpoint matches all active filters
      if (viewSiteFilter || projectFilter || personFilter) {
        const srcMatch = (!viewSiteFilter || sourceNode.site === viewSiteFilter) && (!projectFilter || sourceNode.project_id === projectFilter) && (!personFilter || sourceNode.author_name === personFilter);
        const tgtMatch = (!viewSiteFilter || targetNode.site === viewSiteFilter) && (!projectFilter || targetNode.project_id === projectFilter) && (!personFilter || targetNode.author_name === personFilter);
        if (!srcMatch && !tgtMatch) {
          ctx.globalAlpha = 0.05;
        }
      }

      // D5: an edge touching no source note fades right back — the source nodes carry the highlight.
      if (highlightActive && !highlightedIds!.has(edge.source) && !highlightedIds!.has(edge.target)) {
        ctx.globalAlpha = Math.min(ctx.globalAlpha, 0.05);
      }

      ctx.beginPath();
      ctx.moveTo(sourceNode.x!, sourceNode.y!);
      ctx.lineTo(targetNode.x!, targetNode.y!);

      const isBackbone = edge.similarity < BACKBONE_THRESHOLD;

      if (isConnectedEdge) {
        const edgeColor = colorByCluster
          ? (sourceNode.project_id ? clusterColor(clusterIndexMap.get(sourceNode.project_id)!) : UNCLUSTERED_COLOR)
          : siteColor(sourceNode.site || 'chatgpt');
        ctx.strokeStyle = edgeColor;
        ctx.globalAlpha = Math.min(ctx.globalAlpha, 0.4);
        ctx.lineWidth = 1.5;
      } else {
        // D-40: Default thin connective edges — theme-resolved (was dark-baked rgba(255,255,255,0.06),
        // invisible on warm paper and barely visible even on dark).
        ctx.strokeStyle = themeColors.edge;
        ctx.lineWidth = 1;
      }

      // Repo-backbone link (sub-threshold similarity): dashed + fainter, so a repo's connective tissue is
      // visible but clearly secondary to solid semantic links. setLineDash is reset right after the stroke.
      if (isBackbone) {
        ctx.setLineDash([3, 3]);
        ctx.globalAlpha = Math.min(ctx.globalAlpha, isConnectedEdge ? 0.4 : 0.55);
        ctx.lineWidth = 1;
      }

      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // --- Draw nodes (sorted: largest first for label priority) ---
    const sortedNodes = [...currentNodes].sort((a, b) => b.r - a.r);
    const occupiedRects: { x1: number; y1: number; x2: number; y2: number }[] = [];
    const LABEL_PAD = 6;
    const fontSize = Math.max(8, Math.min(11, 11 / transform.k));

    for (const node of sortedNodes) {
      if (node.x == null || node.y == null) continue;

      const displayRadius = nodeRadius(node.r);
      const color = colorByCluster
        ? (node.project_id ? clusterColor(clusterIndexMap.get(node.project_id)!) : UNCLUSTERED_COLOR)
        : siteColor(node.site || 'chatgpt');
      const isHovered = node.id === hoveredNodeId;
      const isConnected = connectedIds.has(node.id);
      const scale = isHovered ? 1.15 : 1;
      const r = displayRadius * scale;

      // Dim non-connected nodes on hover
      if (hoveredNodeId && !isConnected) {
        ctx.globalAlpha = 0.2;
      } else {
        ctx.globalAlpha = 1;
      }

      // Dim nodes that don't match all active filters, or (D5) that aren't a source of the latest answer.
      const dimmed = !!(viewSiteFilter && node.site !== viewSiteFilter)
        || !!(projectFilter && node.project_id !== projectFilter)
        || !!(personFilter && node.author_name !== personFilter)
        || (highlightActive && !highlightedIds!.has(node.id));

      const nodeColor = dimmed ? themeColors.dimmed : color;

      // Reserve node circle area for collision
      const ringWidth = personColors ? 4 : 0;
      occupiedRects.push({
        x1: node.x - r - ringWidth, y1: node.y - r - ringWidth,
        x2: node.x + r + ringWidth, y2: node.y + r + ringWidth,
      });

      // Person ring (drawn as a thick stroke outside the fill)
      if (personColors && node.author_name && !dimmed) {
        const ringColor = personColors.get(node.author_name);
        if (ringColor) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, r + ringWidth / 2, 0, Math.PI * 2);
          ctx.strokeStyle = ringColor;
          ctx.lineWidth = ringWidth;
          ctx.globalAlpha = hoveredNodeId && !isConnected ? 0.2 : 1;
          ctx.stroke();
        }
      }

      // Node fill
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      ctx.fillStyle = nodeColor;
      ctx.globalAlpha = dimmed ? 0.5 : Math.min(ctx.globalAlpha, isHovered ? 1.0 : 0.8);
      ctx.fill();

      const displayDiameter = r * 2 * transform.k;
      const labelThreshold = LABEL_BASE_THRESHOLD / transform.k;
      const showLabel = isHovered || (isConnected && hoveredNodeId)
        || (!dimmed && !hoveredNodeId && displayDiameter >= labelThreshold);
      if (showLabel) {
        ctx.font = `500 ${fontSize}px "DM Sans", sans-serif`;
        const labelText = isHovered || node.label.length <= LABEL_MAX_CHARS
          ? node.label
          : node.label.slice(0, LABEL_MAX_CHARS - 1) + '…';
        const labelW = ctx.measureText(labelText).width;
        const labelH = fontSize + 2;
        const lx = node.x - labelW / 2;
        const ly = node.y + r + 3;
        const rect = {
          x1: lx - LABEL_PAD, y1: ly,
          x2: lx + labelW + LABEL_PAD, y2: ly + labelH + LABEL_PAD,
        };

        const overlaps = !isHovered && occupiedRects.some(
          o => rect.x1 < o.x2 && rect.x2 > o.x1 && rect.y1 < o.y2 && rect.y2 > o.y1
        );

        if (!overlaps) {
          occupiedRects.push(rect);
          ctx.globalAlpha = 1;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = themeColors.label;
          ctx.fillText(labelText, node.x, ly);
        }
      }

      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }, [canvasRef, nodesRef, edges, hoveredNodeId, adjacency, transform, viewMode, projectFilter, personFilter, personColors, edgeCounts, themeColors, highlightedIds]);

  renderRef.current = render;

  // Initialize / update d3-force simulation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodes.length === 0 || canvasSize.w === 0) return;

    const cx = canvasSize.w / 2;
    const cy = canvasSize.h / 2;

    // Preserve pinned positions (fx/fy) from previous simulation across recreations
    const prevPins = new Map<string, { fx: number | null; fy: number | null }>();
    for (const prev of nodesRef.current) {
      if (prev.fx != null || prev.fy != null) {
        prevPins.set(prev.id, { fx: prev.fx ?? null, fy: prev.fy ?? null });
      }
    }

    // Map node data for simulation (with display radius)
    // Spread initial positions across a wide area proportional to node count
    const nodeCount = nodes.length;
    const spread = Math.sqrt(nodeCount) * 12;
    const simNodes: GraphNode[] = nodes.map((n, i) => {
      const pin = prevPins.get(n.id);
      const angle = (i / nodes.length) * Math.PI * 2 * 5; // spiral
      const dist = Math.sqrt(i / nodes.length) * spread;
      const ec = edgeCounts.get(n.id) || 0;
      return {
        ...n,
        r: nodeRadius(ec),
        x: n.x ?? cx + Math.cos(angle) * dist,
        y: n.y ?? cy + Math.sin(angle) * dist,
        ...(pin ? { fx: pin.fx, fy: pin.fy } : {}),
      };
    });

    // Check if topology changed (new nodes)
    const nodesId = simNodes.map(n => n.id).sort().join(',');
    prevNodesIdRef.current = nodesId;

    // Build links from edge objects
    const links = edges.map(e => ({ source: e.source, target: e.target }));

    // Create or update simulation
    // IMPORTANT: nodes and edges are ALREADY FILTERED by MapSurface (no "you" entries).
    // MapCanvas does NOT re-filter. This prevents forceLink from referencing removed nodes.
    const charge = nodeCount > 200 ? -180 : nodeCount > 50 ? -120 : -80;
    const linkDist = nodeCount > 200 ? 50 : 65;

    const sim = forceSimulation<GraphNode>(simNodes)
      .force('charge', forceManyBody().strength(charge).distanceMax(600))
      // Cross-cluster links pull weakly so the charge force can separate clusters into islands;
      // within-cluster links keep their original cohesion. (Backbone + semantic links treated alike.)
      .force('link', forceLink(links).id((d: any) => d.id).distance(linkDist).strength((l: any) => {
        const a = l.source.project_id;
        return a != null && a === l.target.project_id
          ? INTRA_CLUSTER_LINK_STRENGTH
          : CROSS_CLUSTER_LINK_STRENGTH;
      }))
      .force('cluster', forceCluster(CLUSTER_GATHER_STRENGTH))
      .force('center', forceCenter(cx, cy))
      .force('collide', forceCollide<GraphNode>().radius(d => d.r + 8).iterations(3))
      .force('x', forceX(cx).strength(0.005))
      .force('y', forceY(cy).strength(0.005))
      .alphaDecay(0.02)
      .velocityDecay(0.35)
      .stop();

    // Pre-compute layout headlessly, then reheat at low alpha to mimic drag-settle
    for (let i = 0; i < 200; i++) sim.tick();
    for (let cycle = 0; cycle < 8; cycle++) {
      sim.alpha(0.12);
      for (let i = 0; i < 60; i++) sim.tick();
    }
    nodesRef.current = simNodes;
    renderRef.current();

    // Resume with low alpha for brief settling polish
    sim.alpha(0.05).restart();

    simulationRef.current = sim;

    // Render on each remaining tick
    sim.on('tick', () => {
      nodesRef.current = simNodes;
      renderRef.current();
    });

    // D-37: Start ambient drift after simulation settles
    sim.on('end', () => {
      driftActiveRef.current = true;
    });

    return () => {
      sim.stop();
      simulationRef.current = null;
      driftActiveRef.current = false;
    };
  }, [canvasRef, nodes, edges, canvasSize, simulationRef, nodesRef, edgeCounts]);

  // D-37: Ambient drift loop -- subtle Brownian motion when not interacting
  useEffect(() => {
    let running = true;

    function driftLoop() {
      if (!running) return;

      driftFrameRef.current++;

      // Throttle to ~20fps (every 3rd frame at 60fps rAF)
      if (driftFrameRef.current % DRIFT_FRAME_SKIP === 0 && driftActiveRef.current && !isInteracting) {
        const currentNodes = nodesRef.current;
        for (const node of currentNodes) {
          if (node.fx != null) continue; // Don't drift pinned nodes
          if (node.x != null) node.x += (Math.random() - 0.5) * DRIFT_MAX_DISPLACEMENT * 2;
          if (node.y != null) node.y += (Math.random() - 0.5) * DRIFT_MAX_DISPLACEMENT * 2;
        }
        renderRef.current();
      }

      rafRef.current = requestAnimationFrame(driftLoop);
    }

    rafRef.current = requestAnimationFrame(driftLoop);

    return () => {
      running = false;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isInteracting, nodesRef]);

  // Re-render when transform or hover changes (without re-creating simulation)
  useEffect(() => {
    render();
  }, [render, transform, hoveredNodeId, viewMode]);

  // Canvas resize via ResizeObserver
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        setCanvasSize({ w: width, h: height });
        renderRef.current();
      }
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [canvasRef]);

  return (
    <canvas
      ref={attachCanvas}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        // Transparent: the mounting panel owns the surface color (the redesign's dark map panel).
        background: 'transparent',
        touchAction: 'none',
      }}
    />
  );
}
